import React, { useEffect, useState, useCallback } from "react";
import { openWhatsApp } from "../../lib/whatsapp";
import axios from "axios";
import { Bell, MessageCircle, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = () => axios.create({
  baseURL: API,
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
});

/**
 * OrderNotifBanner
 * ----------------
 * A sticky admin banner that polls /api/admin/notifs every 20s and shows
 * pending online orders (Asporto + Shop) so the staff can tap "Invia
 * WhatsApp" to notify themselves on their phone.
 *
 * Kept dead-simple on purpose: no realtime websockets, no service worker
 * push — just a lightweight poll that also plays a subtle chime the first
 * time a new order arrives while the tab is open.
 */
export default function OrderNotifBanner() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [seenCount, setSeenCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await client().get("/admin/notifs?only_pending=true");
      const rows = data?.items || [];
      // Play a chime + toast if new orders have arrived since last check.
      if (rows.length > seenCount) {
        try {
          const a = new Audio("data:audio/wav;base64,UklGRiIAAABXQVZFZm10IBIAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=");
          a.volume = 0.3; a.play().catch(() => {});
        } catch { /* no audio on some browsers */ }
        toast.success(`🔔 ${rows.length - seenCount} nuovo/i ordine/i in arrivo`);
      }
      setSeenCount(rows.length);
      setItems(rows);
    } catch {
      // silent — banner just won't update if backend hiccups
    }
  }, [seenCount]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const markSent = async (id, waUrl) => {
    // Open WhatsApp first so the user's tap gesture opens the app immediately
    openWhatsApp(waUrl);
    try {
      await client().post(`/admin/notifs/${id}/mark-sent`);
      setItems((rows) => rows.filter((r) => r.id !== id));
    } catch { /* ignore */ }
  };

  const dismiss = async (id) => {
    setItems((rows) => rows.filter((r) => r.id !== id));
    try { await client().post(`/admin/notifs/${id}/dismiss`); } catch { /* ignore */ }
  };
  const dismissAll = async () => {
    setItems([]); setExpanded(false);
    try { await client().post(`/admin/notifs/dismiss-all`); } catch { /* ignore */ }
  };

  if (items.length === 0) return null;

  return (
    <div className="sticky top-0 z-40" data-testid="order-notif-banner">
      <div className={`${items.some((i) => i.kind === "lock_alarm") ? "bg-gradient-to-r from-rose-700 to-rose-900" : "bg-gradient-to-r from-[#4a1c17] to-[#2b1410]"} text-[#f7ebdf] shadow-lg`}>
        <div className="max-w-6xl mx-auto px-3 py-2 flex items-center gap-2">
          <div className="relative">
            <Bell className="h-5 w-5 animate-pulse" />
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] px-1 grid place-items-center">
              {items.length}
            </span>
          </div>
          <div className="flex-1 text-sm font-semibold">
            {items.some((i) => i.kind === "lock_alarm")
              ? "⚠️ Allarme serratura: codice ospite sparito"
              : items.length === 1 ? "1 nuova notifica" : `${items.length} nuove notifiche`}
          </div>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs rounded-full bg-white/10 hover:bg-white/20 px-3 py-1"
            data-testid="notif-toggle"
          >
            {expanded ? "Chiudi" : "Vedi tutti"}
          </button>
          <button
            onClick={dismissAll}
            title="Ignora tutte le notifiche"
            className="h-7 w-7 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center"
            data-testid="notif-dismiss-all"
            aria-label="Chiudi banner"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {expanded && (
          <div className="max-w-6xl mx-auto px-3 pb-3 space-y-1.5">
            {items.map((it) => (
              <div key={it.id} className="rounded-lg bg-white/10 backdrop-blur-sm p-2.5 flex items-center gap-2 text-sm">
                <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${it.kind === "takeaway" ? "bg-amber-200 text-amber-900" : it.kind === "shop" ? "bg-purple-200 text-purple-900" : it.kind === "lock_alarm" ? "bg-rose-600 text-white" : "bg-emerald-200 text-emerald-900"}`}>
                  {it.kind === "takeaway" ? "Asporto" : it.kind === "shop" ? "Shop" : it.kind === "gift" ? "Regalo" : it.kind === "lock_alarm" ? "Allarme" : it.kind}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{it.customer_name ? `${it.customer_name} · € ${Number(it.total || 0).toFixed(2)}` : it.title}</div>
                  <div className="text-[11px] opacity-80 truncate">{it.customer_phone || it.body}</div>
                </div>
                {it.wa_url && (
                <button
                  onClick={() => markSent(it.id, it.wa_url)}
                  data-testid={`notif-send-${it.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1 text-xs font-semibold"
                >
                  <MessageCircle className="h-3 w-3" /> WhatsApp
                </button>
                )}
                <button
                  onClick={() => dismiss(it.id)}
                  data-testid={`notif-dismiss-${it.id}`}
                  title="Ignora"
                  className="h-7 w-7 rounded-full bg-white/10 hover:bg-white/25 grid place-items-center"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
